import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AllowanceDataFragment,
  BulkAssignEmployeesTimeoffPolicyMappingsInput,
  FetchAllowancesWithIncidencesDocument,
  TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument,
  TimeoffPolicyEmployeesCollectionListDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'
import uniqBy from 'lodash/uniqBy'

import { observer } from 'mobx-react'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import AllowanceMapper from 'modules/timeoff/components/TimeoffAllowances/AllowanceMapper'

interface FooterProps {
  loading: boolean
  timeoffPolicyId: number
  employeeIds: number[]
  formState: FormState
  setFormState: (formState: FormState) => void
  onClose: () => void
}

const getTitle = (step: FormState['step']) => {
  switch (step) {
    case 'adjustment_loss':
      return i18n.t('settings.timeoff_policies.adjustment_loss_modal.title')
    case 'select_employees':
      return i18n.t('settings.timeoff_policies.add_employees_modal.assign.title')
    case 'map_allowances':
      return i18n.t('settings.timeoff_policies.manual_policy_mapper.reassign')
  }
}

const getDescription = (step: FormState['step']) => {
  switch (step) {
    case 'adjustment_loss':
      return i18n.t('settings.timeoff_policies.adjustment_loss_modal.description')
    case 'select_employees':
      return i18n.t('settings.timeoff_policies.add_employees_modal.assign.description')
  }
}

const Footer = ({
  loading,
  timeoffPolicyId,
  employeeIds,
  formState,
  setFormState,
  onClose,
}: FooterProps) => {
  const [fetchAllowancesWithIncidences] = useLazyQuery(FetchAllowancesWithIncidencesDocument)
  const [bulkAssignEmployees] = useMutation(TimeoffPolicyAddEmployeeBulkAssignEmployeesDocument, {
    extractorFn: (data) => data.timeoffMutations.bulkAssignEmployeesTimeoffPolicy,
  })

  switch (formState.step) {
    case 'select_employees':
      return (
        <Button
          onClick={async () => {
            const { data } = await fetchAllowancesWithIncidences({
              variables: {
                employeeIds,
              },
            })
            const allowancesToMap = uniqBy(
              compact(
                data?.employees.employees
                  .flatMap((employee) => employee.timeoffAllowanceIncidences)
                  .map((incidence) => incidence.timeoffAllowance)
              ),
              'id'
            )

            if (allowancesToMap.length > 0) {
              setFormState({
                ...formState,
                allowancesToMap,
                step: 'map_allowances',
              })
            } else {
              await bulkAssignEmployees({
                variables: {
                  employeeIds,
                  mappings: [],
                  policyId: timeoffPolicyId,
                },
                refetchQueries: [TimeoffPolicyEmployeesCollectionListDocument],
              })
              onClose()
            }
          }}
          disabled={loading}
        >
          {i18n.t('settings.timeoff_policies.add_employees_modal.button')}
        </Button>
      )
    case 'map_allowances':
      return (
        <Button
          onClick={async () => {
            if (
              formState.mappings.length > 0 &&
              formState.mappings.every((mapping) => mapping.newAllowanceId !== 0)
            ) {
              await bulkAssignEmployees({
                variables: {
                  employeeIds,
                  mappings: formState.mappings,
                  policyId: timeoffPolicyId,
                },
                refetchQueries: [TimeoffPolicyEmployeesCollectionListDocument],
              })
              onClose()
            } else {
              setFormState({
                ...formState,
                step: 'adjustment_loss',
              })
            }
          }}
          disabled={loading}
        >
          {i18n.t('settings.timeoff_policies.add_employees_modal.adjustments.button')}
        </Button>
      )
    case 'adjustment_loss':
      return (
        <Box flexDirection="row" alignItems="center" justifyContent="spaceEvenly" gap="s8">
          <Button
            type="positive"
            onClick={() => {
              setFormState({
                ...formState,
                step: 'map_allowances',
              })
            }}
            disabled={loading}
          >
            {i18n.t('settings.timeoff_policies.adjustment_loss_modal.primary_button')}
          </Button>
          <Button
            type="negative"
            onClick={async () => {
              await bulkAssignEmployees({
                variables: {
                  employeeIds,
                  mappings: formState.mappings,
                  policyId: timeoffPolicyId,
                },
                refetchQueries: [TimeoffPolicyEmployeesCollectionListDocument],
              })
              onClose()
            }}
            disabled={loading}
          >
            {i18n.t('settings.timeoff_policies.adjustment_loss_modal.secondary_button')}
          </Button>
        </Box>
      )
  }
}

interface FormState {
  step: 'select_employees' | 'map_allowances' | 'adjustment_loss'
  allowancesToMap: AllowanceDataFragment[]
  mappings: BulkAssignEmployeesTimeoffPolicyMappingsInput[]
}

interface Props {
  loading: boolean
  timeoffPolicyId: number
  timeoffPolicyEmployeeIds: number[]
  timeoffPolicyAllowances: AllowanceDataFragment[]
  onClose: () => void
}

const AddEmployee = ({
  loading,
  timeoffPolicyId,
  timeoffPolicyEmployeeIds,
  timeoffPolicyAllowances,
  onClose,
}: Props) => {
  const [formState, setFormState] = React.useState<FormState>({
    step: 'select_employees',
    allowancesToMap: [],
    mappings: [],
  })
  const [selectedEmployees, setSelectedEmployees] = React.useState<
    EmployeeForSelectFragmentNoAccess[]
  >([])

  const title = getTitle(formState.step)
  const description = getDescription(formState.step)

  return (
    <Modal
      illustration="alert"
      title={title}
      description={description}
      disableFocusTrap
      onClose={onClose}
      footer={
        <Footer
          loading={loading}
          timeoffPolicyId={timeoffPolicyId}
          employeeIds={selectedEmployees.map((employee) => employee.id)}
          formState={formState}
          setFormState={setFormState}
          onClose={onClose}
        />
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <>
          {formState.step === 'select_employees' && (
            <EmployeeSelector
              value={selectedEmployees}
              onSelect={setSelectedEmployees}
              excludeIds={timeoffPolicyEmployeeIds}
              multiple
            />
          )}
          {formState.step === 'map_allowances' && (
            <AllowanceMapper
              policyId={timeoffPolicyId}
              allowancesToMap={formState.allowancesToMap}
              timeoffPolicyAllowances={timeoffPolicyAllowances}
              onMappingsChange={(mappings) => setFormState({ ...formState, mappings })}
            />
          )}
        </>
      )}
    </Modal>
  )
}

export default observer(AddEmployee)
